import type { Controls, TeachStep } from "@obean/game-contract";

/**
 * A first play that teaches (P5-UX-05): the game's steps (`manifest.teach`), one at a time, over
 * the first time someone plays it, solo or in a room. The platform sees what the player does from
 * the controls it samples and from the game's input (a change in it since the last sample: an
 * aim, a tap, a move or an answer), so it teaches every game the same way without asking the
 * game. Each step waits for what it asks; a step that asks for nothing ("time") moves on after
 * TIME_STEP_MS.
 */

/** How long a step that only says something stays up. */
export const TIME_STEP_MS = 4_000;
/** A step already done moves on after this, so the player sees it was understood. */
export const DONE_HOLD_MS = 600;

const BUTTON = { primary: 1, secondary: 2, ability: 4 } as const;

export class Teacher {
  #at = 0;
  #since: number;
  #doneAt: number | null = null;

  constructor(
    readonly steps: readonly TeachStep[],
    now: number,
  ) {
    this.#since = now;
  }

  /** The step showing now, its number (from 1), and whether it's just been done; null at the end. */
  current(): { step: TeachStep; n: number; of: number; done: boolean } | null {
    const step = this.steps[this.#at];
    return step
      ? { step, n: this.#at + 1, of: this.steps.length, done: this.#doneAt !== null }
      : null;
  }

  /**
   * One sample of what the player did: the controls, and whether the game took it as input. True
   * if the step showing changed (to draw again).
   */
  see(controls: Controls, gaveInput: boolean, now: number): boolean {
    const step = this.steps[this.#at];
    if (!step) return false;
    if (this.#doneAt !== null) {
      if (now - this.#doneAt < DONE_HOLD_MS) return false;
      this.#next(now);
      return true;
    }
    const did =
      step.until === "time"
        ? now - this.#since >= TIME_STEP_MS
        : step.until === "move"
          ? controls.moveX !== 0 || controls.moveY !== 0
          : step.until === "input"
            ? gaveInput
            : (controls.buttons & BUTTON[step.until]) !== 0;
    if (!did) return false;
    if (step.until === "time") {
      this.#next(now);
      return true;
    }
    this.#doneAt = now;
    return true;
  }

  /** The player put it away ("Got it"): no more steps this play. */
  skip(): void {
    this.#at = this.steps.length;
  }

  #next(now: number): void {
    this.#at++;
    this.#since = now;
    this.#doneAt = null;
  }
}

/** The line to show for a step, on a touch screen or not. */
export const lineOf = (step: TeachStep, touch: boolean) =>
  touch && step.sayTouch ? step.sayTouch : step.say;

/**
 * Whether the player did something the game took: its input changed since the last sample. Not
 * "anything but idle": a game may send its standing aim every step, which nobody did just now.
 */
export class InputWatch {
  #last: string | null = null;
  changed(input: unknown): boolean {
    const now = JSON.stringify(input);
    const changed = this.#last !== null && now !== this.#last;
    this.#last = now;
    return changed;
  }
}

/**
 * Whether this browser has had a game's first play (taught, or put away), kept like the age answer:
 * per browser, nothing sent anywhere.
 */
const KEY = (gameId: string) => `obean.taught.${gameId}`;

export function taught(gameId: string, storage: Pick<Storage, "getItem"> | null = safe()): boolean {
  try {
    return storage?.getItem(KEY(gameId)) === "1";
  } catch {
    return false;
  }
}

export function markTaught(
  gameId: string,
  storage: Pick<Storage, "setItem"> | null = safe(),
): void {
  try {
    storage?.setItem(KEY(gameId), "1");
  } catch {
    // No storage (a private window): the first play teaches again next time, which is harmless.
  }
}

function safe(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
